/**
 * @fileoverview WebSocket Timeline Transformer
 * Transforms WebSocket event payloads to timeline format and provides merge functionality
 * for combining real-time WS events with API timeline data.
 */

import { ACTIONS } from '../reducers/game-timeline-parser'

// ============================================================================
// Types for WS Event Payloads (matching backend docs)
// ============================================================================

interface WsImage {
  id: string
  full_path: string
}

interface WsPlayer {
  id: string
  image: WsImage | null
  name: string
  number?: string
}

interface WsAssist {
  id: string
  player: WsPlayer
  type?: 'Assist' | 'Second' | string
}

export interface WsGoalScored {
  goal_id: string
  player: WsPlayer
  team_id: string
  is_home_team: boolean
  assists: WsAssist[]
  period_id: string
  period_number: number
  period_name: string
  period_clock_time: string
  home_team_score: number
  visiting_team_score: number
  goal_type_id?: string
  goal_type?: string
  value?: number
  scored_at: string
}

export interface WsGoalDeleted {
  goal_id: string
  home_team_score: number
  visiting_team_score: number
  deleted_at: string
}

export interface WsShotCreated {
  game_id: string
  shot_id: string
  player: WsPlayer | null
  team_name: string | null
  team_id: string | null
  is_home_team: boolean
  opposing_goalie: WsPlayer | null
  period_number: number | null
  period_name: string | null
  period_clock_time: string | null
  x: number | null
  y: number | null
  created_at: string
}

export interface WsShotDeleted {
  game_id: string
  shot_id: string
  deleted_at: string
}

export interface WsPenaltyCreated {
  offense_id: string
  // New nested player object format
  player?: WsPlayer
  // Legacy flat fields (fallback)
  player_id?: string
  player_name?: string
  player_number?: string | number
  team_id: string
  is_home_team: boolean
  penalty_type?: string
  penalty_type_id?: string
  penalty_minutes?: number | null
  period_id?: string
  period_number: number
  period_name: string
  period_clock_time: string
  served_by_player_id?: string
  served_by_player_name?: string
  served_by_player_number?: string | number
  card?: string
  notes?: string
  offense_severity?: string
  created_at: string
}

export interface WsPenaltyDeleted {
  offense_id: string
  deleted_at: string
}

export interface WsGoalieChanged {
  substitution_id: string
  goalie_in_id?: string
  goalie_in_name?: string
  goalie_in_number?: string | number
  goalie_out_id: string
  goalie_out_name: string
  goalie_out_number?: string | number
  team_id: string
  is_home_team: boolean
  period_id: string
  period_number: number
  period_name?: string
  period_clock_time: string
  created_at: string
}

export interface WsPeriodStarted {
  period_id: string
  period_number: number
  period_name: string
  duration: number
  clock_time: string
  is_overtime: boolean
  started_at: string
}

export interface WsPeriodEnded {
  period_id: string
  period_number: number
  period_name: string
  home_team_score: number
  visiting_team_score: number
  ended_at: string
}

export interface WsGameFinalized {
  game_id: string
  home_team_score: number
  visiting_team_score: number
  game_status_id: number
  started_at: string
  ended_at: string
}

export interface WsPeriodCreated {
  game_id: string
  period_id: string
  period_number: number
  period_name: string
  duration: number
  clock_time: string
  is_overtime: boolean
  started_at: string | null
}

export interface WsPeriodReopened {
  game_id: string
  period_id: string
  period_number: number
  period_name: string
  duration: number
  clock_time: string
  is_overtime: boolean
  started_at: string
}

export interface WsPeriodUpdated {
  game_id: string
  period_id: string
  period_number: number
  period_name: string
  duration: number
  clock_time: string
  is_overtime: boolean
  started_at: string | null
  ended_at: string | null
}

export interface WsShootoutStarted {
  game_id: string
  home_team_score: number
  visiting_team_score: number
  shootout_home_first: boolean
  started_at: string
}

export interface WsShootoutEnded {
  game_id: string
  home_team_score: number
  visiting_team_score: number
  winner: 'home' | 'visiting' | null
  ended_at: string
}

// ============================================================================
// Timeline Event Types (matching game-timeline-parser output)
// ============================================================================

export interface TimelineEvent {
  id: string
  action: string
  playerId?: string
  name?: string
  nameFirst?: string
  nameLast?: string
  number?: string
  teamId: string
  time: string
  gameTime: string
  imageId?: string
  initials?: string
  positionAbbrev?: string
  occurredAt?: string
  // Goal-specific
  assists?: Array<{
    assistId?: string
    id?: string
    fullName?: string
    nameFirst?: string
    nameLast?: string
    number?: string | number
    type?: string
  }>
  goal_type_id?: string
  goal_type?: string
  value?: number
  // Penalty-specific
  duration?: number
  serverId?: string
  serverName?: string
  serverNumber?: string
  type?: string
  typeId?: string
  card?: string
  notes?: string
  severity?: string
  // Goalie change-specific
  nextGoalie?: {
    id?: string
    name?: string
    fullName?: string
    nameFirst?: string
    nameLast?: string
    number?: string | number
    teamId?: string
  }
  // WS-specific metadata
  _isWsEvent?: boolean
  _periodId?: string
  _periodNumber?: number
  _periodName?: string
}

export interface Period {
  id: string
  period_type?: {
    name_full: string
  }
  duration?: number
  events: TimelineEvent[]
  homeShotCount?: number
  visitingShotCount?: number
  homeOffenseCount?: number
  visitingOffenseCount?: number
  goals_home_count?: number
  goals_visiting_count?: number
}

// ============================================================================
// WS Timeline State Types
// ============================================================================

export interface WsTimelineState {
  goals: Record<string, TimelineEvent>
  penalties: Record<string, TimelineEvent>
  goalieChanges: Record<string, TimelineEvent>
  shots: Record<string, TimelineEvent>
  deletedIds: Set<string>
}

export interface WsGameState {
  periodNumber: number | null
  periodName: string | null
  clockTime: string | null
  isOvertime: boolean
  isShootout: boolean
  periodInProgress: boolean | null
  isGameFinalized: boolean
  gameStatusId: number | null
  shootoutWinner: 'home' | 'visiting' | null
  shootoutHomeFirst: boolean | null
  lastEvent:
    | 'period.started'
    | 'period.ended'
    | 'goal.scored'
    | 'goalie.changed'
    | 'penalty.created'
    | 'shot.created'
    | 'score.updated'
    | 'game.finalized'
    | 'period.created'
    | 'period.reopened'
    | 'period.updated'
    | 'shootout.started'
    | 'shootout.ended'
    | null
  lastEventAt: number
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Formats clock time by stripping leading hour zeros
 * e.g., "00:15:30" -> "15:30"
 */
const formatClockTime = (time: string): string => {
  if (!time || typeof time !== 'string') {
    return ''
  }
  const slices = time.split(':')
  if (slices.length === 3 && slices[0] === '00') {
    return slices.slice(1).join(':')
  }
  return time
}

/**
 * Generates initials from name parts
 */
const getInitials = (nameFirst?: string, nameLast?: string): string => {
  let initials = ''
  if (nameFirst) {
    initials += nameFirst[0]
  }
  if (nameLast) {
    initials += nameLast[0]
  }
  return initials.toUpperCase()
}

/**
 * Parses a full name into first and last name parts
 */
const parseFullName = (
  fullName?: string
): { nameFirst: string; nameLast: string } => {
  if (!fullName) {
    return { nameFirst: '', nameLast: '' }
  }
  const parts = fullName.trim().split(' ')
  if (parts.length === 1) {
    return { nameFirst: parts[0], nameLast: '' }
  }
  return {
    nameFirst: parts[0],
    nameLast: parts.slice(1).join(' '),
  }
}

// ============================================================================
// Transform Functions
// ============================================================================

/**
 * Transforms a WebSocket goal.scored event into a timeline event
 */
export function wsGoalToTimelineEvent(event: WsGoalScored): TimelineEvent {
  const { nameFirst, nameLast } = parseFullName(event.player?.name)

  const assists =
    event.assists?.map((assist) => {
      const assistNames = parseFullName(assist.player?.name)
      return {
        assistId: assist.id,
        id: assist.player?.id || assist.id,
        fullName: assist.player?.name,
        nameFirst: assistNames.nameFirst,
        nameLast: assistNames.nameLast,
        number: assist.player?.number || '',
        imageId: assist.player?.image?.id || undefined,
        type: assist.type,
      }
    }) || []

  return {
    id: event.goal_id,
    action: ACTIONS.goal,
    playerId: event.player?.id,
    name: event.player?.name,
    nameFirst,
    nameLast,
    number: event.player?.number || '',
    teamId: event.team_id,
    time: formatClockTime(event.period_clock_time),
    gameTime: event.period_clock_time,
    imageId: event.player?.image?.id || undefined,
    initials: getInitials(nameFirst, nameLast),
    positionAbbrev: '-',
    occurredAt: event.scored_at,
    assists,
    goal_type_id: event.goal_type_id,
    goal_type: event.goal_type,
    value: event.value,
    _isWsEvent: true,
    _periodId: event.period_id,
    _periodNumber: event.period_number,
    _periodName: event.period_name,
  }
}

/**
 * Transforms a WebSocket shot.created event into a timeline event
 */
export function wsShotToTimelineEvent(event: WsShotCreated): TimelineEvent {
  const { nameFirst, nameLast } = parseFullName(event.player?.name)

  return {
    id: event.shot_id,
    action: ACTIONS.shot,
    playerId: event.player?.id,
    name: event.player?.name,
    nameFirst,
    nameLast,
    number: event.player?.number || '',
    teamId: event.team_id || '',
    time: formatClockTime(event.period_clock_time),
    gameTime: event.period_clock_time || '',
    imageId: event.player?.image?.id || undefined,
    initials: getInitials(nameFirst, nameLast),
    positionAbbrev: '-',
    occurredAt: event.created_at,
    _isWsEvent: true,
    _periodNumber: event.period_number,
    _periodName: event.period_name,
  }
}

/**
 * Transforms a WebSocket penalty.created event into a timeline event
 */
export function wsPenaltyToTimelineEvent(
  event: WsPenaltyCreated
): TimelineEvent {
  // Handle nested player object or flat fields
  const playerName = event.player?.name || event.player_name || ''
  const playerId = event.player?.id || event.player_id || ''
  const playerNumber = event.player?.number || event.player_number

  const { nameFirst, nameLast } = parseFullName(playerName)

  return {
    id: event.offense_id,
    action: ACTIONS.penalty,
    playerId,
    name: playerName,
    nameFirst,
    nameLast,
    number: playerNumber?.toString() || '',
    teamId: event.team_id,
    time: formatClockTime(event.period_clock_time),
    gameTime: event.period_clock_time,
    imageId: event.player?.image?.id || undefined,
    initials: getInitials(nameFirst, nameLast),
    positionAbbrev: '-',
    occurredAt: event.created_at,
    duration: event.penalty_minutes ?? undefined,
    serverId: event.served_by_player_id,
    serverName: event.served_by_player_name,
    serverNumber: event.served_by_player_number?.toString(),
    type: event.penalty_type,
    typeId: event.penalty_type_id,
    card: event.card,
    notes: event.notes,
    severity: event.offense_severity,
    _isWsEvent: true,
    _periodId: event.period_id,
    _periodNumber: event.period_number,
    _periodName: event.period_name,
  }
}

/**
 * Transforms a WebSocket goalie.changed event into a timeline event
 */
export function wsGoalieChangeToTimelineEvent(
  event: WsGoalieChanged
): TimelineEvent {
  const { nameFirst: prevNameFirst, nameLast: prevNameLast } = parseFullName(
    event.goalie_out_name
  )
  const { nameFirst: nextNameFirst, nameLast: nextNameLast } = parseFullName(
    event.goalie_in_name
  )

  const nextGoalie = event.goalie_in_id
    ? {
        id: event.goalie_in_id,
        name: event.goalie_in_name,
        fullName: event.goalie_in_name,
        nameFirst: nextNameFirst,
        nameLast: nextNameLast,
        number: event.goalie_in_number?.toString() || '',
        teamId: event.team_id,
      }
    : undefined

  return {
    id: event.substitution_id,
    action: ACTIONS.goalieChange,
    playerId: event.goalie_out_id,
    name: event.goalie_out_name,
    nameFirst: prevNameFirst,
    nameLast: prevNameLast,
    number: event.goalie_out_number?.toString() || '',
    teamId: event.team_id,
    time: formatClockTime(event.period_clock_time),
    gameTime: event.period_clock_time,
    initials: getInitials(prevNameFirst, prevNameLast),
    positionAbbrev: 'G',
    occurredAt: event.created_at,
    nextGoalie,
    _isWsEvent: true,
    _periodId: event.period_id,
    _periodNumber: event.period_number,
    _periodName: event.period_name,
  }
}

// ============================================================================
// Merge Function
// ============================================================================

/**
 * Timeline event sorting function
 * Sorts by gameTime descending, with goals before goalie changes at same time
 */
const timelineSorter = (a: TimelineEvent, b: TimelineEvent): number => {
  if (a.gameTime > b.gameTime) {
    return -1
  } else if (a.gameTime < b.gameTime) {
    return 1
  }

  // Goals should occur before goalie changes at identical clock time
  if (a.action === ACTIONS.goal && b.action === ACTIONS.goalieChange) {
    return -1
  } else if (b.action === ACTIONS.goal && a.action === ACTIONS.goalieChange) {
    return 1
  }

  return 0
}

/**
 * Merges WebSocket timeline events with API timeline data
 * API data takes priority for existing events; WS data adds new/pending events
 *
 * @param apiTimeline - Timeline periods from API
 * @param wsEvents - WebSocket timeline state with events and deleted IDs
 * @returns Merged timeline with WS events integrated
 */
export function mergeWsEventsWithTimeline(
  apiTimeline: Period[],
  wsEvents: WsTimelineState
): Period[] {
  if (!wsEvents) {
    return apiTimeline
  }

  const { goals, penalties, goalieChanges, deletedIds } = wsEvents

  // Early return if no WS events
  const hasWsEvents =
    Object.keys(goals).length > 0 ||
    Object.keys(penalties).length > 0 ||
    Object.keys(goalieChanges).length > 0 ||
    deletedIds.size > 0

  if (!hasWsEvents) {
    return apiTimeline
  }

  // Build set of existing event IDs from API timeline
  const existingEventIds = new Set<string>()
  apiTimeline.forEach((period) => {
    period.events?.forEach((event) => {
      if (event.id) {
        existingEventIds.add(event.id)
      }
    })
  })

  // Collect WS events that don't exist in API (truly new events)
  const newWsEvents: TimelineEvent[] = []

  Object.values(goals).forEach((event) => {
    if (!existingEventIds.has(event.id) && !deletedIds.has(event.id)) {
      newWsEvents.push(event)
    }
  })

  Object.values(penalties).forEach((event) => {
    if (!existingEventIds.has(event.id) && !deletedIds.has(event.id)) {
      newWsEvents.push(event)
    }
  })

  Object.values(goalieChanges).forEach((event) => {
    if (!existingEventIds.has(event.id) && !deletedIds.has(event.id)) {
      newWsEvents.push(event)
    }
  })

  // If no new events and no deletions affecting existing events, return as-is
  if (newWsEvents.length === 0 && deletedIds.size === 0) {
    return apiTimeline
  }

  // Create deep copy of timeline for modification
  const mergedTimeline = apiTimeline.map((period) => ({
    ...period,
    events: [...(period.events || [])],
  }))

  // Build period ID to index mapping
  const periodIndexMap = new Map<string, number>()
  mergedTimeline.forEach((period, index) => {
    if (period.id) {
      periodIndexMap.set(period.id, index)
    }
  })

  // Filter out deleted events from existing periods
  if (deletedIds.size > 0) {
    mergedTimeline.forEach((period) => {
      period.events = period.events.filter((event) => !deletedIds.has(event.id))
    })
  }

  // Add new WS events to appropriate periods
  newWsEvents.forEach((event) => {
    const periodId = event._periodId
    const periodIndex = periodId ? periodIndexMap.get(periodId) : undefined

    if (periodIndex !== undefined) {
      // Add to existing period
      mergedTimeline[periodIndex].events.push(event)
    } else if (event._periodNumber !== undefined) {
      // Period doesn't exist in API yet - find or create stub
      // Try to find by period number
      let targetPeriod = mergedTimeline.find(
        (p) =>
          p.period_type?.name_full?.includes(
            event._periodNumber?.toString() || ''
          ) || p.id === periodId
      )

      if (!targetPeriod && periodId) {
        // Create stub period
        const stubPeriod: Period = {
          id: periodId,
          period_type: {
            name_full: event._periodName || `Period ${event._periodNumber}`,
          },
          events: [event],
        }
        mergedTimeline.push(stubPeriod)
      } else if (targetPeriod) {
        targetPeriod.events.push(event)
      } else {
        // Fallback: add to last period if exists
        if (mergedTimeline.length > 0) {
          mergedTimeline[mergedTimeline.length - 1].events.push(event)
        }
      }
    }
  })

  // Re-sort events within each period
  mergedTimeline.forEach((period) => {
    period.events.sort(timelineSorter)
  })

  return mergedTimeline
}

// ============================================================================
// Initial State Creators
// ============================================================================

/**
 * Creates initial WS timeline state
 */
export function createInitialWsTimelineState(): WsTimelineState {
  return {
    goals: {},
    penalties: {},
    goalieChanges: {},
    deletedIds: new Set(),
    shots: {},
  }
}

/**
 * Creates initial WS game state
 */
export function createInitialWsGameState(): WsGameState {
  return {
    periodNumber: null,
    periodName: null,
    clockTime: null,
    isOvertime: false,
    isShootout: false,
    periodInProgress: null,
    isGameFinalized: false,
    gameStatusId: null,
    shootoutWinner: null,
    shootoutHomeFirst: null,
    lastEvent: null,
    lastEventAt: 0,
  }
}

export default {
  wsGoalToTimelineEvent,
  wsShotToTimelineEvent,
  wsPenaltyToTimelineEvent,
  wsGoalieChangeToTimelineEvent,
  mergeWsEventsWithTimeline,
  createInitialWsTimelineState,
  createInitialWsGameState,
}
